// Cyber posture client (/api/cyber).
//
// Every response carries a `provenance` block naming which fields come from the
// technology estate and which are generated feed-shaped demonstration data. Show
// it — a security screen that blurs the two invites someone to act on a number
// that was never real.

import { get, post, put, AI_TIMEOUT_MS, API_BASE } from "./http";

/** Portfolio posture in one call: counts, bands, lifecycle, compliance, top risks. */
export const getSummary = () => get("/cyber/summary");

/** COTS CVEs. Filters: severity ("HIGH,CRITICAL"), kevOnly, internetFacing, q, productId. */
export const getVulnerabilities = (params = {}) =>
  get("/cyber/vulnerabilities", {
    query: {
      severity: params.severity,
      kev_only: params.kevOnly || undefined,
      internet_facing: params.internetFacing || undefined,
      product_id: params.productId,
      q: params.q,
      limit: params.limit,
    },
  });

/** Products losing or past vendor support. Real lifecycle dates and footprints. */
export const getLifecycle = (windowDays) =>
  get("/cyber/lifecycle", { query: { window_days: windowDays } });

/** Vulnerable open-source components (GitHub/GitLab advisories). */
export const getOpenSource = (params = {}) =>
  get("/cyber/opensource", {
    query: {
      ecosystem: params.ecosystem,
      severity: params.severity,
      fixable_only: params.fixableOnly || undefined,
      direct_only: params.directOnly || undefined,
      source: params.source,
      limit: params.limit,
    },
  });

/** Vendor concentration, FedRAMP authorisation and C-SCRM assessment coverage. */
export const getSupplyChain = () => get("/cyber/supply-chain");

/** Everything known about one product's cyber position. */
export const getProduct = (productId) => get(`/cyber/products/${productId}`);

/** Cyber findings relevant to a demand, matched against the existing estate. */
export const getDemandCyber = (demandId) => get(`/cyber/demands/${demandId}`);

/** The Cyber Analyzer — an AI reading of the posture. Needs working AWS credentials. */
export const analyze = (body = {}) =>
  post("/cyber/analyze", body, { timeout: AI_TIMEOUT_MS });

/** Software bills of materials held, and which products have none. */
export const getSbom = () => get("/cyber/sbom");
export const getSbomDetail = (productId) => get(`/cyber/sbom/${productId}`);

/** Authorities to Operate, soonest expiry first. */
export const getAto = () => get("/cyber/ato");

/** POA&M register. Filters: status ("OPEN,ONGOING"), overdueOnly, productId. */
export const getPoam = (params = {}) =>
  get("/cyber/poam", {
    query: {
      status: params.status,
      overdue_only: params.overdueOnly || undefined,
      product_id: params.productId,
      limit: params.limit,
    },
  });

/** Work a POA&M item. COMPLETED stamps the closure date, which is what makes MTTR measurable. */
export const updatePoam = (poamId, status, note) =>
  put(`/cyber/poam/${poamId}`, { status, note });

/** AI use-case inventory in the shape OMB M-24-10 requires. */
export const getAiSystems = () => get("/cyber/ai-systems");

/** Posture over time and mean time to remediate. */
export const getTrends = (days = 120) => get("/cyber/trends", { query: { days } });

/** Regenerate SBOMs, ATOs, POA&M items, screening and posture history. */
export const seedCompliance = () => post("/cyber/seed", {}, { timeout: AI_TIMEOUT_MS });

/** Every product in compact, chart-ready form (all 27, unlike summary's top 8). */
export const getProducts = () => get("/cyber/products");

/** Direct URL for the AI use-case inventory export.
 *  A link rather than a fetch: the browser should save the file, and routing a
 *  CSV through fetch only to re-wrap it in a blob adds nothing. */
export const aiInventoryExportUrl = (fmt = "csv") =>
  `${API_BASE}/cyber/ai-systems/export?fmt=${encodeURIComponent(fmt)}`;
